<template>
  <nav class="tks" :class="{ 'tks--compact': compact }" aria-label="Task views" data-test="tasks-sidebar">
    <label class="tks__search">
      <span class="tks__sr">Search tasks</span>
      <svg viewBox="0 0 24 24" width="16" height="16" class="tks__svg tks__search-icon" aria-hidden="true" focusable="false">
        <path v-for="(d, i) in ICONS.search" :key="i" :d="d" />
      </svg>
      <input
        class="search-input"
        type="search"
        placeholder="Search tasks"
        autocomplete="off"
        :value="q"
        data-test="tasks-search"
        @input="$emit('update:q', $event.target.value)"
        @keydown.esc="$emit('update:q', '')"
      >
    </label>

    <!-- Narrow screens: the views and the two pickers as native selects in one filter row. -->
    <div v-if="compact" class="tks__row" data-test="tasks-sidebar-compact">
      <label class="tks__field">
        <span class="tks__sr">View</span>
        <select class="tks__select" :value="view" data-test="tasks-view-select" @change="$emit('update:view', $event.target.value)">
          <option v-for="v in views" :key="v.key" :value="v.key">{{ v.label }} ({{ v.count }})</option>
        </select>
      </label>
      <label class="tks__field">
        <span class="tks__sr">Teammate</span>
        <select class="tks__select" :value="emp || ''" data-test="tasks-team-select" @change="$emit('update:emp', $event.target.value || null)">
          <option value="">All teammates</option>
          <option v-for="r in team" :key="r.key" :value="r.key">{{ r.isMe ? 'You' : r.label }} ({{ r.count }})</option>
        </select>
      </label>
      <label class="tks__field">
        <span class="tks__sr">Tag</span>
        <select class="tks__select" :value="tag || ''" data-test="tasks-tag-select" @change="$emit('update:tag', $event.target.value || null)">
          <option value="">All tags</option>
          <option v-for="r in tags" :key="r.key" :value="r.key">#{{ r.label }} ({{ r.count }})</option>
        </select>
      </label>
    </div>

    <template v-else>
      <ul class="tks__views" role="list">
        <li v-for="v in views" :key="v.key">
          <button
            type="button"
            class="tks__item tks__view"
            :class="{ 'is-on': viewOn(v.key) }"
            :aria-current="viewOn(v.key) ? 'page' : null"
            data-test="tasks-view"
            :data-view="v.key"
            @click="$emit('update:view', v.key)"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" class="tks__svg tks__view-icon" aria-hidden="true" focusable="false">
              <path v-for="(d, i) in ICONS[v.key] || []" :key="i" :d="d" />
            </svg>
            <span class="tks__label">{{ v.label }}</span>
            <span class="tks__count" data-test="tasks-view-count">{{ v.count }}</span>
          </button>
        </li>
      </ul>

      <div v-for="g in groups" :key="g.kind" class="tks__group" :data-test="`tasks-${g.kind}`">
        <div class="tks__group-head">
          <button
            type="button"
            class="tks__group-title"
            :aria-expanded="ui[g.kind].collapsed ? 'false' : 'true'"
            title="Collapse or expand"
            :data-test="`tasks-${g.kind}-toggle`"
            @click="toggleCollapsed(g.kind)"
          >
            {{ g.title }}<span class="tks__group-n" :data-test="`tasks-${g.kind}-total`">{{ g.rows.length }}</span>
            <svg viewBox="0 0 24 24" width="13" height="13" class="tks__svg" aria-hidden="true" focusable="false">
              <path :d="ui[g.kind].collapsed ? 'm9 18 6-6-6-6' : 'm6 9 6 6 6-6'" />
            </svg>
          </button>
          <button
            type="button"
            class="tks__group-find"
            :class="{ 'is-on': ui[g.kind].searching }"
            :title="g.findTitle"
            :aria-label="g.findTitle"
            :data-test="`tasks-${g.kind}-find`"
            @click="toggleSearch(g.kind)"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" class="tks__svg" aria-hidden="true" focusable="false">
              <path v-for="(d, i) in ICONS.search" :key="i" :d="d" />
            </svg>
          </button>
        </div>

        <div v-if="ui[g.kind].searching && !ui[g.kind].collapsed" class="tks__find">
          <input
            :ref="`find-${g.kind}`"
            v-model="ui[g.kind].q"
            class="search-input"
            type="search"
            autocomplete="off"
            :placeholder="g.findTitle"
            :aria-label="g.findTitle"
            :data-test="`tasks-${g.kind}-filter`"
            @keydown.enter.prevent="pickFirst(g)"
            @keydown.esc.prevent="closeSearch(g.kind)"
          >
        </div>

        <template v-if="!ui[g.kind].collapsed">
          <ul class="tks__list" role="list">
            <li v-for="r in shownRows(g)" :key="r.key">
              <button
                type="button"
                class="tks__item tks__pick"
                :class="{ 'is-on': isSelected(g, r) }"
                :aria-pressed="isSelected(g, r) ? 'true' : 'false'"
                :data-test="`tasks-${g.kind}-row`"
                :data-key="r.key"
                @click="pick(g, r)"
              >
                <span v-if="g.kind === 'team'" class="tks__avatar" aria-hidden="true">{{ initials(r.label) }}</span>
                <span v-else class="tks__hash" aria-hidden="true">#</span>
                <span class="tks__label">{{ parts(g, r).a }}<mark v-if="parts(g, r).b" class="tks__mark">{{ parts(g, r).b }}</mark>{{ parts(g, r).c }}</span>
                <span class="tks__count">{{ r.count }}</span>
              </button>
            </li>
          </ul>
          <p v-if="searchQuery(g.kind) && !shownRows(g).length" class="tks__empty" :data-test="`tasks-${g.kind}-empty`">
            {{ g.noMatch(ui[g.kind].q.trim()) }}
          </p>
          <button
            v-if="!searchQuery(g.kind) && g.rows.length > g.n"
            type="button"
            class="tks__more"
            :data-test="`tasks-${g.kind}-more`"
            @click="ui[g.kind].all = !ui[g.kind].all"
          >{{ ui[g.kind].all ? 'Show fewer' : `Show all ${g.rows.length}` }}</button>
        </template>
      </div>
    </template>
  </nav>
</template>

<script>
/**
 * TasksSidebar — the redesigned Tasks page's left column (design "Tasks 2a", Phase B1, 2026-10-06).
 *
 *   <tasks-sidebar :views="…" :view.sync="view" :team="…" :tags="…" :emp.sync="emp"
 *                  :tag.sync="tag" :q.sync="q" :team-shown="5" :tags-shown="6" :compact="narrow" />
 *
 * • Search box ("Search tasks") → update:q.
 * • Views (My day · Assigned to me · …) with counts → update:view. A view is drawn selected only
 *   while no teammate / tag is picked (picking one REPLACES the view — the design's "rail").
 * • TEAM / TAGS: header with the total, a collapse chevron and a search icon that turns into a
 *   type-to-filter box (Enter picks the first match, Esc closes it). Below: you + the busiest few
 *   (myDay.js busiest(), the picked row pinned in), "Show all N" / "Show fewer" for the rest.
 *   Clicking the picked row again clears it. → update:emp(key | null), update:tag(key | null).
 * • `compact` (narrow screens): the search box plus three native selects (view, teammate, tag).
 *
 * `team` = myDay.js teamCounts() rows, `tags` = tagCounts() rows, both already sorted.
 * Presentational: no store, no mixin, no $route.
 */
import { busiest } from '@/utils/tasks/myDay.js';

// Lucide shapes (path data only), keyed by view.
const ICONS = {
  search: ['M11 3a8 8 0 1 0 0 16a8 8 0 1 0 0-16', 'm21 21-4.3-4.3'],
  myDay: [
    'M12 8a4 4 0 1 0 0 8a4 4 0 1 0 0-8', 'M12 2v2', 'M12 20v2', 'm4.93 4.93 1.41 1.41', 'm17.66 17.66 1.41 1.41',
    'M2 12h2', 'M20 12h2', 'm6.34 17.66-1.41 1.41', 'm19.07 4.93-1.41 1.41',
  ],
  assignedToMe: [
    'M22 12h-6l-2 3h-4l-2-3H2',
    'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  ],
  createdByMe: ['M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z'],
  upcoming: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'],
  everyone: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 3a4 4 0 1 0 0 8a4 4 0 1 0 0-8',
    'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  completedToday: ['M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20', 'm9 12 2 2 4-4'],
};

/** "Maya Rivera" → "MR". */
function nameInitials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0][0] || '';
  const last = words.length > 1 ? (words[words.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

export default {
  name: 'TasksSidebar',
  props: {
    views: { type: Array, default: () => [] },
    view: { type: String, default: 'myDay' },
    team: { type: Array, default: () => [] },
    tags: { type: Array, default: () => [] },
    emp: { type: String, default: null },
    tag: { type: String, default: null },
    q: { type: String, default: '' },
    teamShown: { type: Number, default: 5 },
    tagsShown: { type: Number, default: 6 },
    compact: { type: Boolean, default: false },
  },

  data() {
    return {
      ICONS,
      ui: {
        team: { collapsed: false, searching: false, q: '', all: false },
        tags: { collapsed: false, searching: false, q: '', all: false },
      },
    };
  },

  computed: {
    groups() {
      return [
        {
          kind: 'team',
          title: 'Team',
          rows: this.team,
          n: this.teamShown,
          selected: this.emp,
          event: 'update:emp',
          findTitle: 'Find a teammate',
          noMatch: q => `No one matches “${q}”`,
        },
        {
          kind: 'tags',
          title: 'Tags',
          rows: this.tags,
          n: this.tagsShown,
          selected: this.tag,
          event: 'update:tag',
          findTitle: 'Find a tag',
          noMatch: q => `No tags match “${q}”`,
        },
      ];
    },
  },

  methods: {
    viewOn(key) {
      return !this.emp && !this.tag && this.view === key;
    },

    labelOf(g, r) {
      return g.kind === 'team' && r.isMe ? 'You' : String(r.label || '');
    },

    initials(name) {
      return nameInitials(name);
    },

    searchQuery(kind) {
      const st = this.ui[kind];
      return st.searching ? st.q.trim().toLowerCase() : '';
    },

    matches(g, r, q) {
      return this.labelOf(g, r).toLowerCase().indexOf(q) > -1 || String(r.label || '').toLowerCase().indexOf(q) > -1;
    },

    shownRows(g) {
      const q = this.searchQuery(g.kind);
      if (q) return g.rows.filter(r => this.matches(g, r, q));
      if (this.ui[g.kind].all) return g.rows;
      return busiest(g.rows, g.n, g.selected);
    },

    /** The label split around the type-to-filter match: { a, b (highlighted), c }. */
    parts(g, r) {
      const label = this.labelOf(g, r);
      const q = this.searchQuery(g.kind);
      const i = q ? label.toLowerCase().indexOf(q) : -1;
      if (i < 0) return { a: label, b: '', c: '' };
      return { a: label.slice(0, i), b: label.slice(i, i + q.length), c: label.slice(i + q.length) };
    },

    isSelected(g, r) {
      return g.selected != null && String(g.selected) === String(r.key);
    },

    pick(g, r) {
      this.$emit(g.event, this.isSelected(g, r) ? null : String(r.key));
    },

    pickFirst(g) {
      const first = this.searchQuery(g.kind) ? this.shownRows(g)[0] : null;
      if (!first) return;
      this.$emit(g.event, String(first.key));
      this.closeSearch(g.kind);
    },

    toggleCollapsed(kind) {
      const st = this.ui[kind];
      st.collapsed = !st.collapsed;
      st.searching = false;
      st.q = '';
    },

    toggleSearch(kind) {
      const st = this.ui[kind];
      st.searching = !st.searching;
      st.q = '';
      if (st.searching) {
        st.collapsed = false;
        this.$nextTick(() => {
          const el = this.$refs[`find-${kind}`];
          const input = Array.isArray(el) ? el[0] : el;
          if (input && typeof input.focus === 'function') input.focus();
        });
      }
    },

    closeSearch(kind) {
      this.ui[kind].searching = false;
      this.ui[kind].q = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.tks {
  display: flex;
  flex-direction: column;
  gap: $fu-space-5;
  min-width: 0;
}

.tks__sr { @include fu-visually-hidden; }

.tks__svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

// ---- Search boxes (plain inputs; ≥ 16px on a phone so iOS does not zoom) ---------------------------

.tks__search {
  position: relative;
  display: block;
  margin: 0;
}

.tks__search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: $fu-text-subtle;
  pointer-events: none;
}

.search-input {
  @include fu-input(42px);
  box-shadow: $fu-shadow-xs;
  &::-webkit-search-cancel-button { cursor: pointer; }
}

.tks__search .search-input { padding-left: 38px; }

.tks__find {
  margin: 0 0 6px;
  .search-input { height: 38px; }
}

// ---- Rows ------------------------------------------------------------------------------------------

.tks__views,
.tks__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tks__item {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border-radius: $fu-radius-md - 1px;
  font-size: $fu-fs-body-sm;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  &:hover { background: rgba(255, 255, 255, 0.7); color: $fu-text; }
  &.is-on {
    background: $fu-surface;
    box-shadow: $fu-shadow-sm;
    font-weight: $fu-fw-bold;
    color: $fu-text;
    .tks__view-icon { color: $fu-brand; }
    .tks__count { color: $fu-text-muted; font-weight: $fu-fw-bold; }
  }
}

.tks__pick { height: 36px; font-size: 13.5px; }

.tks__view-icon { color: $fu-text-subtle; }

.tks__label {
  flex: 1;
  min-width: 0;
  text-align: left;
  @include fu-ellipsis;
}

.tks__count {
  font-size: 12.5px;
  color: $fu-text-subtle;
  @include fu-tabular;
}

.tks__avatar {
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 8.5px;
  font-weight: $fu-fw-bold;
}

.tks__hash {
  flex: none;
  width: 22px;
  text-align: center;
  font-size: 15px;
  color: $fu-text-subtle;
}

.tks__mark {
  padding: 0 1px;
  border-radius: 3px;
  background: $fu-warning-subtle;
  color: inherit;
}

// ---- Group header ---------------------------------------------------------------------------------

.tks__group-head {
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 2px 0 10px;
  margin-bottom: 4px;
}

.tks__group-title {
  @include fu-button-reset;
  @include fu-focus-ring;
  @include fu-eyebrow;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: $fu-radius-xs;
  .tks__svg { color: $fu-text-subtle; }
}

.tks__group-n {
  letter-spacing: 0;
  font-weight: $fu-fw-semibold;
  color: $fu-text-subtle;
}

.tks__group-find {
  @include fu-icon-btn(28px);
  margin-left: auto;
  color: $fu-text-subtle;
  &.is-on { color: $fu-brand; }
}

.tks__empty {
  margin: 0;
  padding: 6px 10px;
  font-size: 13px;
  color: $fu-text-muted;
}

.tks__more {
  @include fu-link-btn;
  margin: 4px 0 0 10px;
  font-size: 13px;
  font-weight: $fu-fw-bold;
}

// ---- Compact (narrow screens): search + one row of selects ------------------------------------------

.tks--compact { gap: $fu-space-3; }

.tks__row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: $fu-space-2;
}

.tks__field {
  display: block;
  margin: 0;
  min-width: 0;
}

.tks__select {
  @include fu-input(40px);
  padding-right: 8px;
  text-overflow: ellipsis;
  -webkit-appearance: menulist;
  appearance: menulist;
}

@include fu-phone {
  .tks__row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .tks__row .tks__field:first-child { grid-column: 1 / -1; }
}
</style>
